import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { DeleteIcon } from "components/icons/delete";
import { EntityBoxHeader } from "../EntityBoxHeader";
import ReactCountryFlag from "react-country-flag";
import { MultiValue, SingleValue } from "react-select";
import { FileInput } from "components/FileInput/FileInput";
import { Formik, Form } from "formik";
import { object, string } from "yup";
import { Countries } from "types/Models/countries";
import { getIsStateRequiredByUuid } from "utils/stateRequired";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { useMemo, useRef } from "react";
import { ArrowDown } from "components/icons/ArrowDown";
import classNames from "classnames";
import { DELAWARE_STATE_UUID, USA_COUNTRY_UUID } from "constants/entity";
import { SUGGESTED_COUNTRIES as SUGGESTED_COUNTRY_NAMES } from "constants/countryCodes";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import {
  AddressFieldValues,
  AddressPicker,
  EMPTY_ADDRESS_FIELDS,
} from "v2/components/AddressPicker/AddressPicker";
import { ENTITY_TYPE_IDS } from "v2/constants/entityTypes";
import { useEntityCreationContext } from "signInSignUp/EntityCreationContext";
import { TaxClassificationField } from "../TaxClassificationField";

export type EntityFormValues = {
  country: string;
  state: string;
  formationType: string;
  entityName: string;
  suffix: string | undefined;
  ein: File | null;
  coiFile: File | null;
  dateOfIncorporation: string | null;
  registeredAgent: string | null;
  taxedAs: string;
  address: AddressFieldValues;
};

type ManualModeProps = {
  disableDelete: boolean;
  onDelete: (isDirty: boolean) => void;
  onCancel: (isDirty: boolean) => void;
  onSave: (values: EntityFormValues) => void;
  initialValues: EntityFormValues;
  countries: Countries[];
  usCountryId?: string;
  isSaved: boolean;
  onEdit: () => void;
  onPreview: (file: File) => void;
};

const buildCountryOption = (country: Countries) => ({
  label: (
    <div className="t-flex t-items-center t-gap-2">
      <ReactCountryFlag countryCode={country.code_alpha_2!} svg />
      <div>{country.name}</div>
    </div>
  ),
  value: country.uuid,
  data: country.name,
});

const SUGGESTED_COUNTRY_ALPHA2: Record<string, string> = {
  "United States": "US",
  "United Kingdom": "GB",
  India: "IN",
};

const getCountryOptionsGrouped = (countries: Countries[]) => {
  const countryByName = new Map(countries.map((c) => [c.name, c]));
  const countryByAlpha2 = new Map(countries.map((c) => [c.code_alpha_2, c]));

  const suggested = SUGGESTED_COUNTRY_NAMES.flatMap((name) => {
    const match =
      countryByName.get(name) ||
      countryByAlpha2.get(SUGGESTED_COUNTRY_ALPHA2[name]);
    if (!match) return [];
    return [
      {
        ...buildCountryOption(match),
        label: (
          <div className="t-flex t-items-center t-gap-2">
            <ReactCountryFlag countryCode={match.code_alpha_2!} svg />
            <div>{name}</div>
          </div>
        ),
      },
    ];
  });
  return [
    { label: "Suggested", options: suggested },
    { label: "All countries", options: countries.map(buildCountryOption) },
  ];
};

const requiredOnceStreetEntered = (message: string) =>
  string().when("streetAddress", {
    is: (streetAddress?: string) => Boolean(streetAddress),
    then: string().required(message),
    otherwise: string().notRequired(),
  });

const ADMIN_FIELDS_SCHEMA = {
  taxedAs: string().when("formationType", {
    is: ENTITY_TYPE_IDS.llc,
    then: string().required("Tax classification is required"),
    otherwise: string().notRequired(),
  }),
  address: object({
    streetAddress: string().notRequired(),
    city: requiredOnceStreetEntered("City is required"),
    state: requiredOnceStreetEntered("State is required"),
    country: requiredOnceStreetEntered("Country is required"),
    zipcode: requiredOnceStreetEntered("Zipcode is required"),
  }),
};

const createValidationSchema = (isFromAdmin: boolean) =>
  object({
    country: string().required("Country is required"),
    state: string().when("country", {
      is: (countryId: string) => countryId === USA_COUNTRY_UUID,
      then: string().required("State is required"),
      otherwise: string().notRequired(),
    }),
    formationType: string().required("Formation type is required"),
    entityName: string().required("Entity name is required"),
    suffix: string().notRequired(),
    ...(isFromAdmin ? ADMIN_FIELDS_SCHEMA : {}),
  });

export const ManualMode = ({
  disableDelete,
  onDelete,
  onCancel,
  onSave,
  initialValues,
  countries,
  usCountryId,
  isSaved,
  onEdit,
  onPreview,
}: ManualModeProps) => {
  const hasMarkedEditingRef = useRef(false);
  const { isFromAdmin } = useEntityCreationContext();

  const countryOptions = useMemo(
    () => getCountryOptionsGrouped(countries),
    [countries]
  );

  const validationSchema = createValidationSchema(isFromAdmin);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSave}
      enableReinitialize
      onReset={() => (hasMarkedEditingRef.current = false)}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ values, setFieldValue, errors, submitForm, dirty }) => {
        // Mark as editing only once when form becomes dirty for saved entities
        if (dirty && isSaved && !hasMarkedEditingRef.current) {
          hasMarkedEditingRef.current = true;
          // Use queueMicrotask to avoid calling setState during render
          queueMicrotask(() => onEdit());
        }

        // Reset flag when entity is saved (dirty becomes false)
        if (!dirty && hasMarkedEditingRef.current) {
          hasMarkedEditingRef.current = false;
        }
        const selectedCountryId = values.country;
        const selectedFormationTypeId = values.formationType;

        const selectedCountry = countries.find(
          ({ uuid }) => uuid === selectedCountryId
        );

        const selectedFormationType =
          selectedCountryId && selectedFormationTypeId
            ? countries
                .find(({ uuid }) => uuid === selectedCountryId)
                ?.formation_type.find((f) => f.uuid === selectedFormationTypeId)
            : null;

        const prefixes = selectedFormationType?.prefixes || [];

        const resetOnCountryChange = () => {
          setFieldValue("state", "");
          setFieldValue("formationType", "");
          setFieldValue("taxedAs", "");
          setFieldValue("address", EMPTY_ADDRESS_FIELDS);
        };

        const onFormationTypeChange = (
          value: SingleValue<OptionData> | MultiValue<OptionData>
        ) => {
          setFieldValue("taxedAs", "");

          if (value instanceof Array || !value) {
            setFieldValue("suffix", "");
            return;
          }

          const selectedFormation = countries
            .find((c) => c.uuid === selectedCountryId)
            ?.formation_type.find((f) => f.uuid === value.value);

          if (
            selectedFormation?.prefixes &&
            selectedFormation.prefixes.length > 0
          ) {
            const selectedFormationPrefixes = selectedFormation.prefixes || [];
            const defaultPrefix =
              selectedFormationPrefixes.find((prefix) => prefix.is_default) ||
              selectedFormationPrefixes[0];
            setFieldValue("suffix", defaultPrefix?.prefix);
          } else {
            setFieldValue("suffix", "");
          }
        };

        const hasChosenUS = values.country === USA_COUNTRY_UUID;
        const allStateOptions =
          selectedCountry?.states.map((state) => ({
            label: state.name,
            value: state.uuid,
          })) || [];

        const PINNED_STATE_UUID = DELAWARE_STATE_UUID; // Delaware

        const pinnedOption = allStateOptions.find(
          (o) => o.value === PINNED_STATE_UUID
        );
        const restOptions = allStateOptions.filter(
          (o) => o.value !== PINNED_STATE_UUID
        );

        const stateOptions = pinnedOption
          ? [pinnedOption, ...restOptions]
          : allStateOptions;

        return (
          <Form>
            <div className="t-border t-border-solid t-border-neutral-10 t-rounded-lg t-p-6">
              <div className="t-flex t-items-center t-justify-between t-mb-4">
                <EntityBoxHeader />
                {!disableDelete && (
                  <Button
                    type="button"
                    customType="ghost"
                    onClick={() => onDelete(dirty)}
                    size="small"
                    leftIcon={<DeleteIcon />}
                  >
                    Delete
                  </Button>
                )}
              </div>

              <div className="t-flex t-flex-col t-gap-4">
                <div
                  className={classNames("t-grid sm:t-grid-cols-1 t-gap-3", {
                    "t-grid-cols-2": hasChosenUS,
                  })}
                >
                  <div>
                    <Combobox
                      label="Select country"
                      required
                      size="regular"
                      components={{
                        ClearIndicator: () => null,
                        GroupHeading: ({ children }: any) => (
                          <div className="t-text-neutral-40 t-text-body-sm t-font-light t-pl-3 t-pr-3 t-pb-1 t-pt-3">
                            {children}
                          </div>
                        ),
                      }}
                      menuPortalTarget={document.body}
                      styles={{
                        menu: () => ({ width: "400px" }),
                      }}
                      onChange={resetOnCountryChange}
                      name="country"
                      withForm
                      shouldValidateOnChange={false}
                      value={
                        selectedCountryId
                          ? buildCountryOption(
                              countries.find(
                                ({ uuid }) => uuid === selectedCountryId
                              )!
                            )
                          : undefined
                      }
                      filterOption={(v, i) =>
                        v.data.data
                          ?.toLocaleLowerCase()
                          .includes(i.toLocaleLowerCase()) || false
                      }
                      options={countryOptions}
                    />
                  </div>

                  {hasChosenUS && (
                    <div>
                      <Combobox
                        label="Select state"
                        required={selectedCountry?.state_required ?? false}
                        size="regular"
                        components={{ ClearIndicator: () => null }}
                        menuPortalTarget={document.body}
                        name="state"
                        withForm
                        shouldValidateOnChange={false}
                        isDisabled={!selectedCountryId}
                        value={
                          values.state && selectedCountry
                            ? selectedCountry.states
                                .map((state) => ({
                                  label: state.name,
                                  value: state.uuid,
                                }))
                                .find((opt) => opt.value === values.state)
                            : undefined
                        }
                        options={stateOptions}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Combobox
                    label="Formation type"
                    required
                    size="regular"
                    components={{ ClearIndicator: () => null }}
                    menuPortalTarget={document.body}
                    name="formationType"
                    withForm
                    shouldValidateOnChange={false}
                    onChange={onFormationTypeChange}
                    isDisabled={!selectedCountryId}
                    value={
                      values.formationType && selectedCountry
                        ? selectedCountry.formation_type
                            .map((formation) => ({
                              label: formation.name,
                              value: formation.uuid,
                            }))
                            .find((opt) => opt.value === values.formationType)
                        : undefined
                    }
                    options={
                      selectedCountry?.formation_type.map((formation) => ({
                        label: formation.name,
                        value: formation.uuid,
                      })) || []
                    }
                  />
                </div>

                {isFromAdmin && <TaxClassificationField />}

                <div className="t-flex-1">
                  <TextInput
                    label="Entity name"
                    name="entityName"
                    required
                    placeholder="Enter entity name"
                    rightComponent={
                      !initialValues.coiFile && prefixes?.length > 0 ? (
                        <Dropdown.Root>
                          <Dropdown.Trigger asChild>
                            <div className="t-flex t-gap-1 t-ml-1">
                              <span className="t-whitespace-nowrap">
                                {values.suffix}
                              </span>
                              <ArrowDown />
                            </div>
                          </Dropdown.Trigger>
                          <Dropdown.Portal container={document.body}>
                            <Dropdown.Content
                              className="t-h-64"
                              side="bottom"
                              sideOffset={10}
                              align="end"
                            >
                              {prefixes.map((prefix) => (
                                <Dropdown.Item
                                  key={prefix.uuid}
                                  onSelect={() =>
                                    setFieldValue("suffix", prefix.prefix)
                                  }
                                >
                                  {prefix.prefix}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Content>
                          </Dropdown.Portal>
                        </Dropdown.Root>
                      ) : null
                    }
                  />
                </div>

                {initialValues.coiFile && (
                  <div>
                    <FileInput
                      label="Certificate of Incorporation"
                      name="coiFile"
                      withForm
                      file={values.coiFile || null}
                      accept={{
                        "application/pdf": [".pdf"],
                        "image/*": [".png", ".jpg", ".jpeg"],
                      }}
                      onDelete={() => onDelete(false)}
                      onFileClick={() =>
                        values.coiFile && onPreview(values.coiFile)
                      }
                    />
                  </div>
                )}

                {isFromAdmin && hasChosenUS && (
                  <AddressPicker
                    name="address"
                    label="Business address"
                    required={false}
                  />
                )}

                {isFromAdmin && (
                  <DateField
                    name="dateOfIncorporation"
                    label="Date of incorporation"
                    placeholder="MMM DD, YYYY"
                  />
                )}

                <div className="t-flex t-justify-end t-gap-2 t-mt-2">
                  {!disableDelete && (
                    <Button type="button" onClick={() => onCancel(dirty)}>
                      Cancel
                    </Button>
                  )}
                  <Button
                    type="button"
                    onClick={submitForm}
                    customType="primary"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
